import { Component, h } from 'preact';
import * as globalStyles from '../../../assets/global.scss';
import Icon from '../../../common/Icon/Icon';
import {
  AirCalendarPrice,
  asDate,
  asDateString,
  getKey,
  Key,
  NBSP,
  PaxAmount,
  Properties,
  TravelDates,
  TravelType,
} from '../../../common';
import { Column, Row } from '../../../common/Layout/Layout';
import { EnvConfig, GtmEvent, GtmService, Navigator, Url } from '../../../config';
import { DateFormat, DateText, PriceFormat, PriceFormatOptions } from '../../../i18n/Format';
import { Modal, MODAL_TITLE, ModalBody, ModalClose, ModalFooter, ModalHeader } from '../../../modal';
import * as componentStyles from '../../../modal/components.scss';
import { BookingState, connectStore, SelectionState } from '../../../store';
import { ClassNames, WithStyles, withStyles } from '../../../style';
import { asCmsLang, asCountryAndLang, isNotPresent, isPresent, rangeFrom } from '../../../utils';
import { bookingFlowDeepLink } from '../../../utils/deeplinkUtils';
import AirCalendarDateHeader from './AirCalendarDateHeader/AirCalendarDateHeader';
import * as styles from './AirCalendarModal.scss';
import { writePreviousSearchesToStore } from '../../../utils/storeUtils';
import { PaxAmountDetails, PaxAmountType } from '../../PaxAmountSelector/PaxAmountDetails/PaxAmountDetails';
import { PointsFormat } from '../../../i18n/Format/PointsFormat';
import { PointsNotification } from '../../Notifications/PointsNotification/PointsNotification';
import { FinText } from '../../../i18n';
import { withText } from 'preact-i18n';

export interface AirCalendarModalProps extends Properties {
  prices: AirCalendarPrice[];
  dateSelected: (travelDates: TravelDates) => any;
  envConfig: EnvConfig;
  selection: SelectionState;
  navigator: Navigator;
  gtmService: GtmService;
  previousSearchesEnabled: boolean;
  isAward: boolean;
}

interface StoreProps {
  paxAmount: PaxAmount;
  instantSearchPrice: string;
}

export type AirCalendarEffectiveProps = AirCalendarModalProps & StoreProps;

export interface AirCalendarState {
  departureDate: string;
  returnDate?: string;
  currentPrice?: AirCalendarPrice;
  cheapestPrice: string;
  hoveredRow: number;
  hoveredColumn: number;
}

const matrixPriceFormatOptions: PriceFormatOptions = {
  abbreviate: true,
  showThousandSeparators: false,
  showSymbol: true,
  showCode: false,
  placeholder: '-',
  trimWhitespace: true,
  useCalendarOptions: true,
};

const buttonPriceFormatOptions: PriceFormatOptions = {
  abbreviate: false,
  showThousandSeparators: true,
  showSymbol: true,
  showCode: false,
  placeholder: NBSP,
  trimWhitespace: false,
};

const mapStateToProps = ({ selection: { paxAmount }, price }: BookingState): StoreProps => ({
  paxAmount,
  instantSearchPrice: price?.startingFrom?.amount,
});

@withText({
  termsLinkUrl: 'promoCode.termsLink.url',
})
@withStyles(componentStyles, styles, globalStyles)
@connectStore({ mapStateToProps })
export default class AirCalendarModal extends Component<AirCalendarModalProps> {
  selectedElement?: HTMLTableCellElement;

  constructor(props: AirCalendarEffectiveProps) {
    super(props);
    const departureDate = props.selection.travelDates.departureDate;
    const returnDate = props.selection.travelDates.returnDate;

    const cheapestPrice = Math.min(...props.prices.map(({ totalPrice }) => parseInt(totalPrice, 10))).toString();

    if (props.prices) {
      this.state = {
        departureDate,
        returnDate,
        currentPrice: this.findPrice(props.prices, departureDate, returnDate),
        cheapestPrice,
      };
    }
  }

  componentDidUpdate(): void {
    this.selectedElement && this.selectedElement.focus();
  }

  componentDidMount(): void {
    const {
      selection: { locale },
      gtmService,
    } = this.props;
    const { country, lang } = asCountryAndLang(locale);
    gtmService.log(GtmEvent.PAGE_VIEW, {
      language: lang,
      path: `/${asCmsLang(locale)}/price-matrix`,
      pointOfSale: country,
    });
  }

  navigateToBooking(
    previousSearchesEnabled: boolean,
    selection: SelectionState,
    navigator: Navigator,
    deepLinkUrl: Url,
    instantSearchPrice: string
  ): void {
    if (previousSearchesEnabled) {
      writePreviousSearchesToStore(selection);
    }
    const state: AirCalendarState = this.state as AirCalendarState;
    const airCalendarPrice = state.currentPrice?.pricePerAdult;
    navigator.navigateToBooking(deepLinkUrl, {
      instantSearchPrice,
      airCalendarPrice,
    });
  }

  render({
    prices,
    tx,
    dateSelected,
    envConfig,
    selection,
    navigator,
    previousSearchesEnabled,
    paxAmount,
    isAward,
    instantSearchPrice,
  }: AirCalendarEffectiveProps & WithStyles): h.JSX.Element {
    const { departureDate, returnDate } = selection.travelDates;
    const earliestDepartureDate: string = datePlusDays(departureDate, -3);
    const earliestReturnDate: string = isPresent(returnDate) && datePlusDays(returnDate, -3);
    const state: AirCalendarState = this.state as AirCalendarState;
    const deepLinkUrl: Url = isPresent(state.departureDate)
      ? bookingFlowDeepLink(
          envConfig,
          {
            ...selection,
            travelDates: { departureDate: state.departureDate, returnDate: state.returnDate },
          },
          isAward
        )
      : undefined;

    return (
      <Modal wide class={tx('aircalendar')}>
        <ModalHeader compact class={tx('titleArea border-bottom')}>
          <Row class={tx(this.props.class)} hCenter grow={false}>
            <div class="flex-1 nordic-blue-900-text font-heading-5" id={MODAL_TITLE}>
              <FinText id="airCalendar.flexibleTravelDates" />
            </div>
            <ModalClose />
          </Row>
        </ModalHeader>
        <ModalBody>
          {isAward && <PointsNotification open />}
          <table
            class={tx('aircalendar-table')}
            onMouseLeave={() => {
              this.clearHover();
            }}
          >
            <tr>
              {isPresent(returnDate) && <td class={tx('left-side-filler grey-100-bg')} />}
              {isPresent(returnDate) && <td class={tx('grey-100-bg')} />}
              <td class={tx('nordic-blue-900-bg white-text font-body-1 z1')}>
                <div class={tx('departure-date-title nowrap medium-type pr-xxsmall-l ps-xxsmall-y')}>
                  <Icon iconName="departure" class={tx('is-xsmall flex--nogrow')} />
                  <span class={tx('ms-xxsmall-l')}>
                    <FinText id={`dateSelector.departure.title`} />
                  </span>
                </div>
              </td>
              {rangeFrom(0, 6).map((col) => (
                <td key={col} class={tx('nordic-blue-900-bg')} />
              ))}
            </tr>
            <tr>
              {isPresent(returnDate) && <td class={tx('left-side-filler grey-100-bg')} />}
              {isPresent(returnDate) && <td class={tx('grey-100-bg')} />}
              {rangeFrom(0, 7).map((col) => {
                const date: string = datePlusDays(earliestDepartureDate, col);
                return (
                  <AirCalendarDateHeader
                    key={date}
                    dateString={date}
                    isHighlighted={
                      col === state.hoveredColumn || (state.hoveredColumn === undefined && date === state.departureDate)
                    }
                    accessibilityScope={'col'}
                  />
                );
              })}
            </tr>

            {selection.travelType !== TravelType.ONE_WAY ? (
              rangeFrom(0, 7).map((row) => {
                const date: string = datePlusDays(earliestReturnDate, row);
                return (
                  <tr key={date}>
                    <td class={tx('return-date-title left-side-filler relative nordic-blue-900-bg')}>
                      {row === 0 && (
                        <span class={tx('absolute nowrap white-text font-body-1 z1 medium-type pr-xsmall-r')}>
                          <Icon iconName="arrival" class={tx('is-xsmall flex--nogrow')} />
                          <span class={tx('ms-xxsmall-l')}>
                            <FinText id={`dateSelector.return.title`} />
                          </span>
                        </span>
                      )}
                    </td>
                    <AirCalendarDateHeader
                      dateString={date}
                      isHighlighted={
                        row === state.hoveredRow || (state.hoveredRow === undefined && date === state.returnDate)
                      }
                      accessibilityScope={'row'}
                    />
                    {rangeFrom(0, 7).map((col) => {
                      return this.priceElement(
                        earliestDepartureDate,
                        col,
                        earliestReturnDate,
                        row,
                        prices,
                        dateSelected,
                        tx,
                        state
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                {isPresent(returnDate) && <td>{NBSP}</td>}
                {rangeFrom(0, 7).map((col) => {
                  return this.priceElement(earliestDepartureDate, col, undefined, 0, prices, dateSelected, tx, state);
                })}
              </tr>
            )}
          </table>
        </ModalBody>
        <ModalFooter class={tx('ps-large-x')} sticky={isPresent(deepLinkUrl)}>
          <Column class={tx('paxAmount nordic-blue-900-text font-body-1')}>
            <div class={tx('flex')}>
              <div class={tx('font-body-1')}>
                <div>
                  <span class={tx('ps-xxsmall-r')}>
                    <FinText id="startingPrice.pricesFrom" />
                  </span>
                  <span class={tx('nordic-blue-900-text medium-type')}>
                    {state.currentPrice?.totalPoints ? (
                      <PointsFormat totalPoints={state.currentPrice.totalPoints} showSuffix />
                    ) : (
                      <PriceFormat
                        price={
                          state.currentPrice
                            ? { amount: state.currentPrice.totalPrice, currencyCode: state.currentPrice.currency }
                            : undefined
                        }
                        options={buttonPriceFormatOptions}
                      />
                    )}
                  </span>
                </div>
                <PaxAmountDetails type={PaxAmountType.DETAILED} paxAmount={paxAmount} />
              </div>
              <div class={tx('df-tablet-up')}>
                {state.departureDate && this.selectedDate(state.departureDate, 'departure', tx)}
                {state.returnDate && this.selectedDate(state.returnDate, 'return', tx)}
              </div>
            </div>
          </Column>
          <button
            tabIndex={51}
            class={tx(
              this.props.class,
              'bw-aircalendar-proceed-button',
              'ctaButton',
              'ps-medium bold-type font-body-1 nowrap continueMinWidth'
            )}
            disabled={isNotPresent(deepLinkUrl)}
            onClick={() =>
              this.navigateToBooking(previousSearchesEnabled, selection, navigator, deepLinkUrl, instantSearchPrice)
            }
          >
            <FinText id="continueButton.search" />
          </button>
        </ModalFooter>
      </Modal>
    );
  }

  private selectedDate(date: string, bound: 'departure' | 'return', tx: ClassNames): h.JSX.Element {
    const icon = bound === 'return' ? 'arrival' : 'departure';

    return (
      <div class={tx('flex font-body-1 ps-medium-l')}>
        <div>
          <Icon iconName={icon} class={tx('is-xsmall flex--nogrow')} />
        </div>
        <div>
          <div class={tx('dn-mobile')}>
            <FinText id={`airCalendar.${bound}Title`} />
          </div>
          <div class={tx('medium-type')}>
            <DateText value={date} format={DateFormat.DATE_AND_MONTH_FULL} />
          </div>
        </div>
      </div>
    );
  }

  private priceElement(
    earliestDepartureDate: string,
    col: number,
    earliestReturnDate: string,
    row: number,
    prices: AirCalendarPrice[],
    dateSelected: (travelDates: TravelDates) => any,
    tx: ClassNames,
    state: AirCalendarState
  ): h.JSX.Element {
    const departureDate: string = datePlusDays(earliestDepartureDate, col);
    const returnDate: string = isPresent(earliestReturnDate) ? datePlusDays(earliestReturnDate, row) : undefined;
    const price: AirCalendarPrice = this.findPrice(prices, departureDate, returnDate);
    const hasPrice: boolean = isPresent(price);
    const isReturn: boolean = isPresent(state.returnDate);
    const isSelected: boolean = isReturn
      ? departureDate === state.departureDate && returnDate === state.returnDate
      : departureDate === state.departureDate;
    const isHighlightedForSelect: boolean = isReturn
      ? (departureDate === state.departureDate && returnDate <= state.returnDate) ||
        (departureDate <= state.departureDate && returnDate === state.returnDate)
      : departureDate === state.departureDate;
    const isCheapest = state.cheapestPrice === price?.totalPrice;

    const activeHover = state.hoveredColumn !== undefined && state.hoveredRow !== undefined;
    const isHighlightedForHover =
      (col === state.hoveredColumn && row <= state.hoveredRow) ||
      (isReturn && row === state.hoveredRow && col <= state.hoveredColumn);
    const isHighlighted = isHighlightedForHover || (!activeHover && isHighlightedForSelect);
    const tabIndex = 7 * row + col + 1;
    return (
      <td
        class={tx('price-elem border font-body-1', {
          'link-hover': hasPrice,
          'is-selected': isSelected,
          'nordic-blue-900-text': !isCheapest,
          'heather-700-text': isCheapest,
          'medium-type': isCheapest,
          'bold-type': isSelected,
          'has-price': hasPrice,
          'nordic-blue-50-bg': isHighlighted,
        })}
        ref={(element) => {
          if (isSelected) {
            this.selectedElement = element;
          }
        }}
        id={`row${row}col${col}`}
        tabIndex={hasPrice ? tabIndex : null}
        onClick={() => hasPrice && this.dateSelected(dateSelected, departureDate, returnDate)}
        onKeyPress={(e: KeyboardEvent): void => {
          const key: Key = getKey(e);
          if (hasPrice && (key === Key.ENTER || key === Key.SPACE)) {
            this.dateSelected(dateSelected, departureDate, returnDate);
          }
        }}
        onKeyDown={(e: KeyboardEvent): void => {
          this.handleKeyboardNavigation(getKey(e), row, col, tabIndex);
        }}
        onMouseEnter={(): void => {
          if (hasPrice) {
            this.dateHovered(row, col);
          } else {
            this.clearHover();
          }
        }}
      >
        <span
          class={tx('bw-price flex flex--center grow fill', {
            'ps-medium-y': !isReturn,
            absolute: isReturn,
          })}
        >
          {price?.totalPoints || price?.totalPrice ? (
            price?.totalPoints ? (
              <PointsFormat totalPoints={price.totalPoints} abbreviate />
            ) : (
              <PriceFormat
                price={price ? { amount: price.totalPrice, currencyCode: price.currency } : undefined}
                options={matrixPriceFormatOptions}
              />
            )
          ) : (
            <Icon iconName="flights-unavailable" class={tx('ir-xxsmall flex--nogrow grey-500-text')} />
          )}
        </span>
      </td>
    );
  }

  private handleKeyboardNavigation(key: Key, row, col, tabIndex): void {
    if (key === Key.ARROW_LEFT || key === Key.ARROW_RIGHT) {
      const elems = document.getElementsByClassName('bw-price-elem bw-has-price');

      if (key === Key.ARROW_LEFT && !(row === 0 && col === 0)) {
        let prevElem = elems.item(0);

        for (let i = 1; i < elems.length; i++) {
          const currElem = elems.item(i);

          if (currElem && parseInt(currElem.getAttribute('tabindex'), 10) >= tabIndex) {
            document.getElementById(prevElem.id)?.focus();
            break;
          }

          prevElem = currElem;
        }
      }

      if (key === Key.ARROW_RIGHT && !(row === 6 && col === 6)) {
        let prevElem = elems.item(elems.length - 1);
        for (let i = elems.length - 2; i >= 0; i--) {
          const currElem = elems.item(i);

          if (currElem && parseInt(currElem.getAttribute('tabindex'), 10) <= tabIndex) {
            document.getElementById(prevElem.id)?.focus();
            break;
          }

          prevElem = currElem;
        }
      }
    }

    if (key === Key.ARROW_UP && row > 0) {
      document.getElementById(`row${row - 1}col${col}`)?.focus();
    }

    if (key === Key.ARROW_DOWN && row < 6) {
      document.getElementById(`row${row + 1}col${col}`)?.focus();
    }
  }

  private findPrice(data: AirCalendarPrice[], departureDate: string, returnDate?: string): AirCalendarPrice {
    return isPresent(returnDate)
      ? data.find((p) => p.departureDate === departureDate && p.returnDate === returnDate)
      : data.find((p) => p.departureDate === departureDate);
  }

  private dateHovered(row: number, col: number) {
    this.setState(() => ({
      hoveredRow: row,
      hoveredColumn: col,
    }));
  }

  private clearHover() {
    this.setState(() => ({
      hoveredRow: undefined,
      hoveredColumn: undefined,
    }));
  }

  private dateSelected(
    dateSelected: (travelDates: TravelDates, isFlexibleCalendar: boolean) => any,
    departureDate: string,
    returnDate?: string
  ): void {
    const { prices } = this.props as AirCalendarModalProps;
    this.setState(() => ({
      departureDate,
      returnDate,
      currentPrice: this.findPrice(prices, departureDate, returnDate),
    }));
    dateSelected({ departureDate, returnDate, flexible: true }, true);
  }
}

function datePlusDays(date: string, days: number): string {
  const d: Date = asDate(date);
  d.setUTCDate(d.getUTCDate() + days);
  return asDateString(d);
}
